import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AlertService, AuthenticationService } from '../../_services/index';
import { User } from '../../_models/index';
import { Image } from '../../_models/image';
import { AESEncryptDecryptService } from '../../_services/aesencrypt-decrypt.service';
import {environment} from '../../../environments/environment';
import {Category} from '../../_models/category';
import { Constants } from 'src/app/constants/constants';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import {Location} from '@angular/common';
import {UtilsService} from '../../_services/utils.service';
import {buildSquareAuthorizeUrl} from '../../_services/square-oauth.util';
import {resolveLandingPath} from '../../_helpers/role-landing';
import {isLegacyTvUserAgent} from '../../_helpers/legacy-tv.util';

@Component({
  selector: 'app-login-page',
  styleUrls: ['login.component.scss'],
  templateUrl: 'login.component.html'
})
export class LoginComponent implements OnInit {
  Constants = Constants;
  loginForm: FormGroup;
  loading = false;
  passwordResetEmailMissing = false;
  submitted = false;
  returnUrl: string;
  error = false;
  currentUser: User;
  images: Image[] = [];
  tableUuid: string;
  tableName: string;
  localMerchantId: any;
  errorMessage: any;
  storedMerchantId: string;
  storedCloverToken: string;
  categoriesFirstMerchants: string[];
  categoriesFirstSwitch: boolean;
  cloverCategories: Category[] = [];
  // Device-mode boot screen: when /login?isDevice=true is opened on a paired
  // POS device, hide the login form entirely and show a "device booting"
  // loader instead. The cashier never sees a login form on a device — a
  // separate unlock popup gates access once the staff session is restored.
  isDeviceLoading = false;
  // Max time SW cleanup may delay the confirmation-display redirect.
  private static readonly SW_CLEANUP_WATCHDOG_MS = 2500;
  // True whenever localStorage.isDeviceMode==='true' (paired POS device).
  // Drives the credential-input copy: "PIN or password" instead of
  // "Password" so the cashier can paste a 4-digit clockInPin.
  isDeviceMode = false;
  // Backend lockout banner. The BE returns the literal string
  // "Too many PIN attempts. Try password or wait 10 minutes." when the
  // PIN-only retry counter trips. Surface it inline (not via the generic
  // <alert>) so the user knows password is still an option.
  pinLockoutMessage = '';


  constructor(
    private formBuilder: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private authenticationService: AuthenticationService,
    private _AESEncryptDecryptService: AESEncryptDecryptService,
    private utilsService: UtilsService,
    private alertService: AlertService) {

    this.currentUser = utilsService.getCurrentUser();

    this.categoriesFirstMerchants = ['invalid id'];
    this.categoriesFirstSwitch = true;

  }

  ngOnInit() {

    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required]
    });
    this.performUrlParamsInit();
  }

  private performUrlParamsInit() {
    const url_string = window.location.href;
    const url = new URL(url_string);

    // Navigating to /login always resets any sticky `isDeviceMode` flag
    // so a paired device can't get stuck on the boot loader after being
    // bounced back to the login page. The URL flag below re-arms it for
    // this visit only — the URL is the source of truth, not localStorage.
    localStorage.removeItem('isDeviceMode');

    if (url.searchParams.get('table')) {
      localStorage.setItem('table', url.searchParams.get('table'));
    }
    // isDevice URL flag: persist BEFORE auto-login so the post-login
    // routing in routeToProperComponent picks up device-mode and lands
    // the user on the full-page register instead of the admin sidebar.
    //   `?isDevice=true|1` → enable device-mode + show loader
    //   anything else (incl. `?isDevice=false`, empty, missing)
    //                       → leave device-mode cleared (already done above)
    const rawIsDevice = url.searchParams.get('isDevice');
    if (rawIsDevice === 'true' || rawIsDevice === '1') {
      localStorage.setItem('isDeviceMode', 'true');
      // Render the device-boot loader instead of the login form for
      // this attempt. Auto-login (handled below when email+password
      // come through the URL) will navigate away from /login on
      // success. If the device wasn't given creds, the loader stays
      // up — that's the desired appliance look.
      // A normal native first boot must still show the login form. Only hide
      // it when credentials were supplied for an automatic device login.
      this.isDeviceLoading = !!((url.searchParams.get('email') || url.searchParams.get('username'))
        && url.searchParams.get('password'));
    }
    // After the URL/sticky flag dance is done, mirror the persisted
    // device-mode flag into the component so the template can swap the
    // credential placeholder ("PIN or password"). This stays accurate even if the
    // loader path above ran (in which case the form is hidden anyway).
    this.isDeviceMode = localStorage.getItem('isDeviceMode') === 'true';
    if ((url.searchParams.get('email') || url.searchParams.get('username')) && url.searchParams.get('password')) {
      const username = url.searchParams.get('email').toLowerCase() ? url.searchParams.get('email').toLowerCase() : url.searchParams.get('username').toLowerCase();
      this.loginForm.controls.username.setValue(username);
      this.loginForm.controls.password.setValue(url.searchParams.get('password'));
      this.onSubmit(url);
    }
    const isMobile = url.searchParams.get('mobile') === 'true'
      || url.searchParams.get('mobile') === '1'
      || url.searchParams.get('isMobile') === 'true'
      || url.searchParams.get('isMobile') === '1';
    if (isMobile) {
      this.capacitorStoageSet();
      return;
    } else {

      // get return url from route parameters or default to '/'
      this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    }
    this.deleteQueryParameterFromCurrentRoute();
  }

// convenience getter for easy access to paymentForm fields
  get f() {
    return this.loginForm.controls;
  }

  onSubmit(url?) {
    // Capture isDeviceMode set by performUrlParamsInit before the clear
    // below wipes it — the post-login routing in routeToProperComponent
    // depends on it to send admins to /cashier instead of the sidebar.
    // performUrlParamsInit is now the single source of truth for the
    // flag (URL on this visit only; sticky behavior was removed).
    const persistedIsDeviceMode = localStorage.getItem('isDeviceMode');
    this.authenticationService.logout();
    localStorage.clear();
    sessionStorage.clear();
    const table = localStorage.getItem('table');
    if (url && url.searchParams.get('table')?.toLowerCase()) {
      localStorage.setItem('table', url.searchParams.get('table').toLowerCase());
    }
    if (table) {
      localStorage.setItem('table', table);
    }
    if (persistedIsDeviceMode === 'true') {
      localStorage.setItem('isDeviceMode', 'true');
    }
    this.error = false;
    this.submitted = true;
    // stop here if paymentForm is invalid
    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;
    this.authenticationService.login(this.f.username.value.toLowerCase(), this.f.password.value)
      .subscribe(
        async (data: any) => {
          await this.setCapacitorUser(this._AESEncryptDecryptService.encrypt(JSON.stringify(data.user)));

          // The backend returns a Clover OAuth redirect when the merchant's
          // Clover credentials need attention, even though the username /
          // password login itself succeeded and data.user already contains a
          // valid DineNGo JWT. A browser may follow that redirect as before.
          // The Android Runner must not: doing so interrupts the completed
          // DineNGo login, leaves the cashier in Clover's browser flow, and
          // produces a split session that later looks expired on /dashboard.
          // Clover Go owns its separate OAuth flow when a reader is connected.
          const deferCloverOAuthToRunner = Capacitor.isNativePlatform()
            && data.user?.orgSettings?.accountType === Constants.CLOVER;

          if (data.redirectURL && !deferCloverOAuthToRunner) {
            const url = new URL(data.redirectURL);
            if (!(data.user && data.user.orgSettings?.accountType === Constants.CLOVER) && url.searchParams.get('user')) {
              url.searchParams.set('user', this._AESEncryptDecryptService.encrypt(url.searchParams.get('user')));
              data.redirectURL = url;
            }
            this.utilsService.setCurrentUser(data.user, data.user.role[0]);
            // Square QA P0: If the backend is handing us a Square hosted
            // OAuth URL (because the org has no/expired credentials), we
            // must NOT navigate to the raw URL — it has no per-attempt
            // CSRF state. Rebuild it through buildSquareAuthorizeUrl so
            // a fresh state is stored in sessionStorage before we leave.
            if (data.user?.orgSettings?.accountType === Constants.SQUARE) {
              window.location.assign(buildSquareAuthorizeUrl());
              return;
            }
            window.location.assign(data.redirectURL);
          } else if (!data.user && data.orgSettings?.accountType === Constants.SQUARE) {
            this.routeToProperSquareComponent(data.role);
          } else {
            if (data.user?.role.length > 1) {
              this.router.navigate(['/select-role']);
            } else {
              this.utilsService.setSelectedRole(data.user.role[0]);
              localStorage.setItem('CartItem', this._AESEncryptDecryptService.encrypt(JSON.stringify([])));
              this.routeToProperComponent(data.user.role[0], data.user);
            }
          }
          await this.setCapacitorUser(this._AESEncryptDecryptService.encrypt(JSON.stringify(data.user)));

        },
        error => {
          this.error = true;
          this.loading = false;
          // PIN lockout banner: the BE returns the literal string when the
          // per-user PIN-only retry counter trips. Surface it inline so the
          // user knows password is still allowed, instead of the generic
          // "incorrect" alert. Look for it in the standard error-payload
          // shapes (HttpErrorResponse.error.message, plain string, etc).
          const lockoutMessage = this.extractLockoutMessage(error);
          if (lockoutMessage) {
            this.pinLockoutMessage = lockoutMessage;
          } else {
            this.pinLockoutMessage = '';
            this.alertService.error(error);
          }
          // Auto-login failure recovery: if the loader was up because
          // a deep link supplied bad creds (or the network is down),
          // dropping it here lets the cashier see the form + error
          // alert instead of a perpetual "Starting device…" screen.
          // The form's `<alert>` is rendered inside `*ngIf="!isDeviceLoading"`,
          // so it can only surface once the loader is down.
          this.isDeviceLoading = false;
        });

    console.log('in login.component.ts OnSubmit completed1'); // *MES* - doesnt reach here mostly.
  }

  /**
   * Pull the BE PIN-lockout sentinel out of the error envelope, if present.
   * Returns the friendly banner string when the BE response carries the
   * "Too many PIN attempts…" message, else empty so the caller falls back
   * to the generic alert. Matches the substring "PIN attempts" so we don't
   * silently miss a future copy tweak.
   */
  private extractLockoutMessage(error: any): string {
    if (!error) { return ''; }
    const candidates: any[] = [
      error?.error?.message,
      error?.error,
      error?.message,
      error,
    ];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && /pin attempts/i.test(candidate)) {
        return candidate;
      }
    }
    return '';
  }

  private routeToProperComponent(role, user?: User) {
    // Admin/Manager/Server may have a user-configurable landing path
    // (Bug 2). For other roles the destination is fixed by the app.
    if (role === Constants.ADMIN || role === Constants.MANAGER || role === Constants.SERVER) {
      // isDevice device-mode override: land staff straight on the
      // standalone full-page register (no sidebar). The device never
      // logs out — a lock-screen popup gates access instead.
      if (localStorage.getItem('isDeviceMode') === 'true') {
        this.router.navigate(['/cashier']);
        return;
      }
      const saved = user?.preferences?.defaultLandingPath;
      const target = resolveLandingPath(role, saved);
      const reload = role === Constants.ADMIN; // preserve historical reload-on-admin behaviour
      this.router.navigate([target]).then(() => {
        if (reload) {
          window.location.reload();
        }
      });
      return;
    }
    if (role === Constants.TABLE) {
      this.router.navigate(['/tableSetup'])
        .then(() => {
          // window.location.reload();
        });
    } else if (role === 'Rewards') {
      this.router.navigate(['/rewards'])
        .then(() => {
          // window.location.reload();
        });
    } else if (role === Constants.KIOSK) {
      this.router.navigate(['/kiosk']).then(() => {
        // window.location.reload();

      });
    } else if (role === Constants.JUKEBOX) {
      this.router.navigate(['/youtube']).then(() => {
        // window.location.reload();

      });
    } else if (role === Constants.QR_ORDER) {
      this.router.navigate(['/QROrder']).then(() => {
        // window.location.reload();

      });
    } else if (role === 'PaymentsDisplay') {
      this.router.navigate(['/paymentUser/paymentUser']).then(() => {
        // window.location.reload();
      });
    } else if (role === Constants.ORDERS_DISPLAY) {
      this.router.navigate(['/orderUser/orderUser']).then(() => {
        // window.location.reload();
      });

    } else if (role === Constants.CONFIRMATION_DISPLAY) {
      this.openConfirmationDisplayFresh();

    } else {
      this.router.navigate(['/login'], { queryParamsHandling: 'preserve' }).then(() => {
      });
    }
  }

  /**
   * Confirmation displays stay open for months. Unregister any Angular service
   * worker before the role transition, then hard-navigate so this kiosk always
   * executes the current app shell rather than a cached pre-deploy bundle.
   */
  private openConfirmationDisplayFresh(): void {
    const target = '/confirmation-display';
    if (this.isLegacyTvBrowser()) {
      // Full page loads are unreliable on LG NetCast, so stay inside the
      // already-booted SPA. The TV DOES run service workers (router logs
      // show ngsw prefetching bundles from the TV — that prefetch starves
      // page loads and wedged boot), so also purge any registration
      // best-effort WITHOUT blocking: NetCast SW promises can hang forever,
      // and the SPA navigation must never wait on them.
      const tvRegistrations = this.getServiceWorkerRegistrations();
      if (tvRegistrations) {
        tvRegistrations
          .then(regs => Promise.all(regs.map(registration => registration.unregister())))
          .catch(() => { /* best-effort — never blocks the navigation below */ });
      }
      this.router.navigateByUrl(target, { replaceUrl: true });
      return;
    }
    let navigated = false;
    const go = () => {
      if (navigated) { return; }
      navigated = true;
      this.navigateToUrl(target);
    };
    const registrations = this.getServiceWorkerRegistrations();
    if (registrations) {
      // LG NetCast (Chrome 53) exposes the ServiceWorker API but its
      // getRegistrations()/unregister() promises can hang forever, leaving
      // the TV on "Starting device…" — SW cleanup is best-effort and must
      // never block the redirect.
      setTimeout(go, LoginComponent.SW_CLEANUP_WATCHDOG_MS);
      registrations
        .then(regs => Promise.all(regs.map(registration => registration.unregister())))
        .then(go)
        .catch(go);
      return;
    }
    go();
  }

  // Seam for tests — UA sniff for the legacy LG drive-through TVs.
  // Shared with app.module's ngsw `enabled` gate so both always agree.
  isLegacyTvBrowser(ua: string = (typeof navigator !== 'undefined' && navigator.userAgent) || ''): boolean {
    return isLegacyTvUserAgent(ua);
  }

  // Seam for tests — window.location is unforgeable in browsers.
  navigateToUrl(url: string): void {
    window.location.replace(url);
  }

  // Seam for tests. Returns null when no usable SW API exists.
  getServiceWorkerRegistrations(): Promise<readonly ServiceWorkerRegistration[]> | null {
    try {
      if ('serviceWorker' in navigator && navigator.serviceWorker
          && typeof navigator.serviceWorker.getRegistrations === 'function') {
        return navigator.serviceWorker.getRegistrations();
      }
    } catch (_) {
      // A legacy browser must still be allowed to enter the display.
    }
    return null;
  }

  getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') { c = c.substring(1, c.length); }
      if (c.indexOf(nameEQ) === 0) {
        return true;
      }
    }
    return false;
  }

  private routeToProperSquareComponent(role) {
    if ((role === 'Admin')) {
      window.location.href = `${environment.base_url}/square/sidebar/admin/dashboard`;
    } else if (role === 'Manager' || role === Constants.SERVER) {
      window.location.href = `${environment.base_url}/sidebar/cashier`;
    } else if (role === Constants.TABLE) {
      window.location.href = `${environment.base_url}/square/sidebar/sidebar/order`;
    } else if (role === Constants.KIOSK) {
      window.location.href = `${environment.base_url}/square/kiosk`;
    }  else {
      this.router.navigate(['login'], { queryParamsHandling: 'preserve' }).then(() => {
      });
    }
  }

  reauthenticateRouter(buttonType) {
    if (buttonType === Constants.CLOVER) {
      this.reAuthenticateClover();
    } else if (buttonType === Constants.SQUARE) {
      this.reAuthenticateSquare();
    }
  }

  reAuthenticateSquare() {
    // Generates a fresh CSRF state per attempt and sends lowercase session=false.
    // Square QA P0: state must be uniquely generated per connection attempt,
    // and `session` is case-sensitive (must be `false`, not `False`).
    window.location.assign(buildSquareAuthorizeUrl());
  }

  reAuthenticateClover() {
      window.location.assign(`${environment.cloverAuthUrl}?client_id=${environment.client_id}`); // reroutes to get oauth
  }

  resetPassword() {
    this.passwordResetEmailMissing = false;
    const email = this.f.username.value;

    if (email) {
      this.authenticationService.generateOTC(email).subscribe(data => {
        this.router.navigate(['/reset-password',  { email: email, id: data.id, number: data.number}]);
      }, err => {
        this.errorMessage = err;
        setTimeout(() => {
          // set waited to true
          this.errorMessage = '';
        }, 7000);
      });
    } else {
      this.passwordResetEmailMissing = true;
    }
  }

  async getObject() {
    const ret = await Preferences.get({ key: 'currentUser' });
    const user = ret?.value ? this._AESEncryptDecryptService.decrypt(ret.value) : null;
    return user;
  }

  private async capacitorStoageSet() {
    const user = await this.getObject();
    if (user) {
      this.currentUser = JSON.parse(user);
      this.utilsService.setCurrentUser(this.currentUser);
      this.routeToProperComponent(this.currentUser.role[0], this.currentUser);
    }
  }

  async setCapacitorUser(value) {
    await Promise.all([
      Preferences.set({ key: 'user', value }),
      Preferences.set({ key: 'currentUser', value }),
    ]);
  }

  deleteQueryParameterFromCurrentRoute() {
    // this.location.replaceState('./order');
    this.location.replaceState(window.location.href.split('?')[0].split(window.location.href.split('/')[2])[1]);
  }
}
